import z from "zod";
import { definePluginConfig } from "@/plugins/server/builder";
import type { PluginConfig } from "@/plugins/server/types";

export const generationPluginConfigDef = definePluginConfig({
  schema: z.object({
    voiceDetection: z
      .object({
        /**
         * VAD score threshold for detecting voice activity during speech (0.0-1.0).
         * Uses hysteresis: once voice is detected, this higher threshold must be exceeded
         * to continue considering audio as speech. This prevents flickering between
         * speech/silence states during continuous speaking.
         */
        scoreInThreshold: z.number().prefault(0.5),
        /**
         * VAD score threshold for detecting end of voice activity (0.0-1.0).
         * When voice is active, audio must fall below this lower threshold to be
         * considered silence. Lower than `scoreInThreshold` to provide hysteresis.
         */
        scoreOutThreshold: z.number().prefault(0.25),
        /**
         * Number of silent audio chunks to buffer before voice starts (default: 100 ≈ 1s).
         * These chunks are emitted when voice is detected, ensuring the first syllables
         * and word onsets are captured for better STT accuracy.
         *
         * Can slightly impact STT latency, as increases the amount of audio to be processed.
         */
        prePaddingChunks: z.number().prefault(100),
        /**
         * Number of silent chunks to append after voice ends before finalizing (default: 200 ≈ 2s).
         *
         * Most STT providers require silence padding to finalize transcription. For example,
         * Deepgram with `endpointing: 0` and `no_delay: true` still needs substantial
         * silence to return results. Too few chunks may cause the STT to hang indefinitely.
         *
         * This default (200) balances latency and stability across providers. If your STT
         * finalizes transcripts quickly, consider lowering this value. Benchmark carefully.
         */
        postPaddingChunks: z.number().prefault(200),
        /**
         * Minimum duration (in ms) of continuous voice to trigger agent interruption.
         * Uses a sliding window to accumulate voice chunks and filter out VAD false positives.
         * Only when accumulated voice duration exceeds this threshold, the agent will be interrupted.
         */
        minVoiceInterruptionMs: z.number().prefault(50),
      })
      .prefault({}),
    endOfTurnDetection: z
      .object({
        /**
         * Probability threshold (0.0-1.0) that determines when the user has finished speaking.
         * If EOU model confidence >= threshold, agent responds immediately. Otherwise, waits
         * with an adaptive timeout (see min/maxTimeoutMs below).
         *
         * Tuning considerations:
         * - Too low: Agent may interrupt users mid-sentence, creating awkward overlaps
         * - Too high: Agent waits longer before responding, increasing perceived latency
         * - Default (0.6): Balanced trade-off between responsiveness and avoiding interruptions
         */
        threshold: z.number().prefault(0.6),
        /**
         * Fallback timeout (in ms) ensuring the agent eventually responds even when EOU
         * confidence stays low. Prevents the agent from waiting indefinitely if the model
         * never reaches the threshold (e.g., incomplete sentences, uncertain patterns).
         */
        minTimeoutMs: z.number().prefault(300),
        /**
         * Maximum wait time (in ms) when EOU confidence is at or near zero. As confidence
         * increases, the timeout shrinks adaptively toward minTimeoutMs using:
         * `timeout = max(minTimeoutMs, maxTimeoutMs * (1 - probability / threshold))`
         *
         * This creates natural turn-taking: high confidence = quick response, low confidence = patient waiting.
         */
        maxTimeoutMs: z.number().prefault(5000),
      })
      .prefault({}),
  }),
});

export type GenerationPluginConfig<T extends "input" | "output"> = PluginConfig<
  typeof generationPluginConfigDef,
  T
>;
